/**
 * CONFIGURACIÓN CENTRAL DE NAVEGACIÓN Y PERMISOS
 * 
 * Este archivo es la ÚNICA FUENTE DE VERDAD para:
 * 1. El menú de navegación principal (AdminDashboard.jsx)
 * 2. El sistema de permisos por roles (RolesTab.jsx)
 * 3. El editor de permisos granulares (PermisosGranularesEditor.jsx)
 * 
 * INSTRUCCIONES:
 * - Para añadir una nueva pestaña: añádela al array MENU_PRINCIPAL
 * - Para cambiar el orden: reordena los elementos del array
 * - Para añadir submenús: usa la propiedad 'submenus'
 * - Los permisos se sincronizarán automáticamente
 */

import {
  Users, Calendar, CalendarDays, FileText, UserPlus, Clock,
  Package, Settings, DollarSign, TrendingUp, CheckSquare,
  Receipt, Lock, Briefcase, Dumbbell, Wrench, Building2, MonitorPlay,
  ClipboardList, LayoutGrid, Stethoscope, Mail, Bell, Monitor, Wallet, Eye
} from 'lucide-react';

/**
 * ESTRUCTURA DEL MENÚ PRINCIPAL
 * 
 * Cada elemento puede tener:
 * - id: identificador único (usado para permisos)
 * - label: texto que se muestra en el menú
 * - icon: icono de lucide-react
 * - group: grupo para organización visual (opcional)
 * - altPermission: permiso alternativo para acceso (opcional)
 * - submenus: array de submenús con permisos específicos (opcional)
 *   - id: identificador del submenú (formato: 'padre.hijo')
 *   - label: texto del submenú
 *   - acciones: array de acciones permitidas ['ver', 'crear', 'editar', 'eliminar']
 *              si no se especifica, se permiten todas las acciones
 */
export const MENU_PRINCIPAL = [
  // ========== FILA 1 ==========
  {
    id: 'clients',
    label: 'Clientes',
    icon: Users,
    group: 'clientes',
    submenus: [
      { id: 'clients.exportar', label: 'Exportar CSV', acciones: ['ver'] },
      { id: 'clients.importar', label: 'Importar CSV', acciones: ['crear'] },
    ]
  },
  { id: 'controles', label: 'Controles', icon: ClipboardList, group: 'clientes' },
  { id: 'clases-prueba', label: 'Clases Prueba', icon: UserPlus, group: 'clientes' },
  { id: 'paquetes-continuo8', label: 'Continuo 8', icon: Package, group: 'clientes' },
  { id: 'horarios-fijos', label: 'Horarios Fijos', icon: Calendar, group: 'clientes' },
  { id: 'horarios-semanales', label: 'Semanales', icon: CalendarDays, group: 'clientes' },

  // ========== FILA 2 ==========
  {
    id: 'cuadrante',
    label: 'Cuadrante',
    icon: LayoutGrid,
    group: 'horarios',
    submenus: [
      { id: 'cuadrante.profesionales', label: 'Profesionales' },
      { id: 'cuadrante.salas', label: 'Salas' },
      { id: 'cuadrante.tratamientos', label: 'Tratamientos' },
      { id: 'cuadrante.jornadas', label: 'Jornadas' },
      { id: 'cuadrante.bloqueos', label: 'Bloqueos' },
    ]
  },
  { id: 'global', label: 'Global', icon: CalendarDays, group: 'horarios' },
  // Tablero-maqueta de jornadas y clases (Ann 2026-10-20). soloAnn: NO es un permiso
  // granular: solo la ve la administradora (AdminDashboard lo filtra por correo).
  { id: 'planificacion', label: 'Planificación', icon: LayoutGrid, group: 'horarios', soloAnn: true },
  {
    id: 'bonos',
    label: 'Bonos',
    icon: Package,
    group: 'horarios',
    submenus: [
      { id: 'bonos.tipos', label: 'Tipos de Bono' },
      { id: 'bonos.sesiones', label: 'Sesiones' },
      { id: 'bonos.vender', label: 'Vender Bonos', acciones: ['crear'] },
    ]
  },
  { id: 'consultas', label: 'Citas', icon: Receipt, group: 'horarios' },
  { id: 'comercial', label: 'Comercial', icon: TrendingUp, group: 'horarios' },
  { id: 'comercial-v2', label: 'Comercial v2', icon: TrendingUp, group: 'horarios' },
  { id: 'bonificaciones', label: 'Bonificaciones', icon: Package, group: 'clientes' },
  { id: 'taquillas', label: 'Taquillas', icon: Lock, group: 'horarios' },
  {
    id: 'contable_agrupada',
    label: 'Contable',
    icon: Briefcase,
    group: 'horarios',
    altPermission: ['cobros-presenciales', 'precios']
  },
  { id: 'caja', label: 'Control de Caja', icon: Wallet, group: 'horarios' },
  { id: 'clientes-empresa', label: 'Cliente Empresa', icon: Building2, group: 'clientes' },

  // ========== FILA 3 ==========
  {
    id: 'clinica',
    label: 'Clínica',
    icon: Stethoscope,
    group: 'horarios',
    submenus: [
      { id: 'clinica.ficha_paciente', label: 'Ficha de Paciente', acciones: ['ver', 'editar'] },
      { id: 'clinica.valoracion', label: 'Visita de Valoración', acciones: ['ver', 'crear', 'editar'] },
      { id: 'clinica.historia', label: 'Historia Clínica' },
      { id: 'clinica.cuestionarios', label: 'Cuestionarios', acciones: ['ver', 'crear', 'editar'] },
      { id: 'clinica.consultas', label: 'Consultas Fisio' },
      { id: 'clinica.informes_ia', label: 'Informes IA', acciones: ['ver', 'crear'] },
    ]
  },
  {
    id: 'tecnica',
    label: 'Técnica',
    icon: Dumbbell,
    group: 'tecnica',
    submenus: [
      { id: 'tecnica.clases', label: 'Clases' },
      { id: 'tecnica.ejercicios', label: 'Ejercicios' },
      { id: 'tecnica.programaciones', label: 'Programaciones' },
    ]
  },
  { id: 'canal-videos', label: 'CANAL VIDEOS', icon: MonitorPlay, group: 'otros' },
  // FISIOVISION · antesala del superadmin de FisioVisión (producto aparte). Aquí se
  // administra el MATERIAL de los ejercicios (el «almacén»); la ficha del paciente
  // seguirá en su sitio. Submenús para permisos granulares; la navegación real vive
  // dentro de FisioVisionTab. (Ann, 15-ago-2026)
  {
    id: 'fisiovision', label: 'FISIOVISION', icon: Eye, group: 'otros',
    submenus: [
      { id: 'fisiovision.pautas', label: 'Pautas' },
      { id: 'fisiovision.pautas.videos', label: 'Pautas · Vídeos' },
      { id: 'fisiovision.pautas.ilustraciones', label: 'Pautas · Ilustraciones' },
      { id: 'fisiovision.entender', label: 'Entender' },
      { id: 'fisiovision.cuestionarios', label: 'Cuestionarios' },
      { id: 'fisiovision.motivaciones', label: 'Motivaciones' },
    ]
  },
  { id: 'contabilidad-2', label: 'Contabilidad', icon: Briefcase, group: 'otros', altPermission: 'gestoria' },
  {
    id: 'email-marketing',
    label: 'Email Marketing',
    icon: Mail,
    group: 'otros',
    submenus: [
      { id: 'email-marketing.plantillas', label: 'Plantillas' },
      { id: 'email-marketing.campanas', label: 'Campañas', acciones: ['ver', 'crear', 'editar'] },
      { id: 'email-marketing.enviar', label: 'Enviar campañas', acciones: ['crear'] },
      { id: 'email-marketing.estadisticas', label: 'Estadísticas', acciones: ['ver'] },
    ]
  },
  {
    id: 'email-marketing-ia',
    label: 'Email Marketing IA',
    icon: Mail,
    group: 'otros',
    submenus: [
      { id: 'email-marketing-ia.preparar', label: 'Preparar correos con IA', acciones: ['ver'] },
      { id: 'email-marketing-ia.enviar', label: 'Enviar (prueba y envío real)', acciones: ['crear'] },
      { id: 'email-marketing-ia.registro', label: 'Registro de envíos', acciones: ['ver'] },
    ]
  },
  { id: 'historial-notificaciones', label: 'Notificaciones', icon: Bell, group: 'otros' },

  // ========== FILA 4 ==========
  { id: 'cambios', label: 'Cambios', icon: FileText, group: 'otros' },
  { id: 'estadisticas', label: 'Estadísticas', icon: TrendingUp, group: 'otros' },
  { id: 'configuracion-general', label: 'Configuración', icon: Settings, group: 'config' },
  {
    id: 'config_clinica',
    label: 'Config. Clínica',
    icon: Settings,
    group: 'config-clinica',
    submenus: [
      { id: 'config_clinica.protocolos', label: 'Protocolos Clínicos' },
      { id: 'config_clinica.zonas_corporales', label: 'Zonas Corporales' },
      { id: 'config_clinica.elementos', label: 'Elementos de Exploración' },
      { id: 'config_clinica.cuestionarios', label: 'Cuestionarios Personalizados' },
    ]
  },
  // Médicos que derivan (Ann 03-11-2026): acceso directo a la lista única de
  // médicos + estadísticas. Mismo permiso que Config. Clínica.
  { id: 'medicos_derivan', label: 'Médicos que derivan', icon: Stethoscope, group: 'config-clinica', altPermission: 'config_clinica' },
  {
    id: 'config_tecnica',
    label: 'Config. Técnica',
    icon: Wrench,
    group: 'config-tecnica',
    submenus: [
      { id: 'config_tecnica.clases', label: 'Clases' },
      { id: 'config_tecnica.ejercicios', label: 'Ejercicios' },
      { id: 'config_tecnica.adaptaciones', label: 'Adaptaciones' },
      { id: 'config_tecnica.musicas', label: 'Músicas' },
    ]
  },
  {
    id: 'laboral',
    label: 'Laboral',
    icon: Clock,
    group: 'config',
    submenus: [
      { id: 'laboral.nominas', label: 'Nóminas' },
      { id: 'laboral.profesionales', label: 'Profesionales' },
      { id: 'laboral.profesiones', label: 'Profesiones' },
      { id: 'laboral.complementarias', label: 'Complementarias' },
      { id: 'laboral.registros_mes', label: 'Registros del Mes' },
      { id: 'laboral.autonomos', label: 'Autónomos' },
      { id: 'laboral.eventos', label: 'Eventos' },
    ]
  },
  // ========== MI PUESTO (zona privada de cada trabajador: cada usuario SOLO ve lo suyo) ==========
  { id: 'mi-puesto', label: 'Mi puesto', icon: Monitor, group: 'otros', alwaysVisible: true },

  // ========== SEGURIDAD (pestaña reservada, visible sin permisos) ==========
  { id: 'seguridad-vacia', label: 'Seguridad', icon: Lock, group: 'otros', alwaysVisible: true },

  // ========== ENTRADAS OCULTAS (no visibles en menú) ==========
  { id: 'cobros-pendientes', label: 'Cobros Pendientes', icon: DollarSign, group: 'horarios', hidden: true },
  { id: 'cobros-presenciales', label: 'Cobros Presenciales', icon: DollarSign, group: 'horarios', hidden: true },
  // ⛔ 'hoja-contable' RETIRADA (legacy/PROHIBIDA · marcada para eliminar · Ann 2026-07-10). Fuera de menús (admin y editor).
  { id: 'precios', label: 'Precios', icon: DollarSign, group: 'horarios', hidden: true },
  { id: 'validacion-caja', label: 'Validación Caja', icon: CheckSquare, group: 'otros', hidden: true },
  {
    id: 'gestoria',
    label: 'PENDIENTE',
    hidden: true,
    icon: Briefcase,
    group: 'horarios',
    altPermission: 'gestoria.control',
    submenus: [
      { id: 'gestoria.ingresos', label: 'Ingresos y Facturación', acciones: ['ver', 'crear', 'editar', 'eliminar'] },
      { id: 'gestoria.gastos', label: 'Gastos e IA', acciones: ['ver', 'crear', 'editar', 'eliminar'] },
      { id: 'gestoria.sepa', label: 'Recibos y SEPA', acciones: ['ver', 'crear', 'editar', 'eliminar'] },
      { id: 'gestoria.tesoreria', label: 'Tesorería', acciones: ['ver', 'crear', 'editar', 'eliminar'] },
      { id: 'gestoria.diario', label: 'Diario y Cierre', acciones: ['ver', 'crear', 'editar', 'eliminar'] },
      { id: 'gestoria.configuracion', label: 'Configuración Contable' },
    ]
  },
];

/**
 * FUNCIONALIDADES ESPECIALES
 * Acciones que no están ligadas a una pestaña específica
 */
export const FUNCIONALIDADES_ESPECIALES = [
  { id: 'cobrar_citas', label: 'Cobrar citas', icon: DollarSign },
  { id: 'vender_bonos', label: 'Vender bonos', icon: Package },
  { id: 'programar_bonos', label: 'Programar bonos', icon: Calendar },
  { id: 'cancelar_citas', label: 'Cancelar citas' },
  { id: 'exportar_datos', label: 'Exportar datos', icon: FileText },
  { id: 'validar_caja', label: 'Validar caja', icon: CheckSquare },
  { id: 'enviar_emails_masivos', label: 'Enviar emails masivos', icon: Mail },
  { id: 'ver_totales_economicos', label: 'Ver totales económicos', icon: TrendingUp },
];

/**
 * FUNCIONES AUXILIARES
 */

// Contar total de permisos (pestañas + submenús)
export const contarTotalPermisos = () => {
  let total = MENU_PRINCIPAL.length;
  MENU_PRINCIPAL.forEach(item => {
    if (item.submenus) {
      total += item.submenus.length;
    }
  });
  return total;
};

// Obtener todas las pestañas como array plano (para compatibilidad)
export const obtenerPestanasPlanas = () => {
  return MENU_PRINCIPAL.map(item => ({
    id: item.id,
    label: item.label,
    icon: item.icon,
  }));
};

// Generar estructura de permisos vacía
export const generarPermisosVacios = () => {
  const permisos = { especiales: {} };

  // Agrupar por secciones para compatibilidad con backend
  const secciones = {
    clientes: ['clients', 'controles', 'clases-prueba', 'paquetes-continuo8', 'horarios-fijos', 'horarios-semanales'],
    agenda: ['cuadrante', 'global', 'bonos', 'consultas'],
    contabilidad: ['cobros-pendientes', 'cobros-presenciales', 'contabilidad', 'precios', 'validacion-caja', 'estadisticas'],
    clinica: ['clinica'],
    tecnica: ['tecnica'],
    taquillas: ['taquillas'],
    gestoria: ['gestoria'],
    otros: ['cambios', 'email-marketing', 'email-marketing-ia', 'historial-notificaciones', 'canal-videos', 'contabilidad-2'],
    configuracion: ['configuracion-general', 'config_clinica', 'config_tecnica', 'laboral'],
  };

  Object.entries(secciones).forEach(([seccionKey, ids]) => {
    permisos[seccionKey] = {};
    ids.forEach(id => {
      permisos[seccionKey][id] = { ver: false, crear: false, editar: false, eliminar: false };
      // Añadir submenús
      const menuItem = MENU_PRINCIPAL.find(m => m.id === id);
      if (menuItem?.submenus) {
        menuItem.submenus.forEach(sub => {
          permisos[seccionKey][sub.id] = { ver: false, crear: false, editar: false, eliminar: false };
        });
      }
    });
  });

  FUNCIONALIDADES_ESPECIALES.forEach(esp => {
    permisos.especiales[esp.id] = false;
  });

  return permisos;
};

// Generar permisos completos (para admin)
export const generarPermisosCompletos = () => {
  const permisos = generarPermisosVacios();

  Object.keys(permisos).forEach(seccionKey => {
    if (seccionKey === 'especiales') {
      Object.keys(permisos.especiales).forEach(key => {
        permisos.especiales[key] = true;
      });
    } else {
      Object.keys(permisos[seccionKey]).forEach(pestanaId => {
        permisos[seccionKey][pestanaId] = { ver: true, crear: true, editar: true, eliminar: true };
      });
    }
  });

  return permisos;
};

// Mapeo de secciones para encontrar dónde está una pestaña
export const SECCION_POR_PESTANA = {};
const secciones = {
  clientes: ['clients', 'controles', 'clases-prueba', 'paquetes-continuo8', 'horarios-fijos', 'horarios-semanales'],
  agenda: ['cuadrante', 'global', 'bonos', 'consultas'],
  contabilidad: ['cobros-pendientes', 'cobros-presenciales', 'contabilidad', 'precios', 'validacion-caja', 'estadisticas'],
  clinica: ['clinica'],
  tecnica: ['tecnica'],
  taquillas: ['taquillas'],
  gestoria: ['gestoria'],
  otros: ['cambios', 'email-marketing', 'email-marketing-ia', 'historial-notificaciones', 'canal-videos', 'contabilidad-2'],
  configuracion: ['configuracion-general', 'config_clinica', 'config_tecnica', 'laboral'],
};

Object.entries(secciones).forEach(([seccionKey, ids]) => {
  ids.forEach(id => {
    SECCION_POR_PESTANA[id] = seccionKey;
    // También mapear submenús
    const menuItem = MENU_PRINCIPAL.find(m => m.id === id);
    if (menuItem?.submenus) {
      menuItem.submenus.forEach(sub => {
        SECCION_POR_PESTANA[sub.id] = seccionKey;
      });
    }
  });
});

// Encontrar la sección de una pestaña
export const findSeccionByPestanaId = (pestanaId) => {
  // Primero buscar directamente
  if (SECCION_POR_PESTANA[pestanaId]) {
    return SECCION_POR_PESTANA[pestanaId];
  }
  // Si es un submenú, buscar por el padre
  const parentId = pestanaId.split('.')[0];
  return SECCION_POR_PESTANA[parentId] || null;
};

export default {
  MENU_PRINCIPAL,
  FUNCIONALIDADES_ESPECIALES,
  contarTotalPermisos,
  obtenerPestanasPlanas,
  generarPermisosVacios,
  generarPermisosCompletos,
  findSeccionByPestanaId,
  SECCION_POR_PESTANA,
};
